<script lang="ts">
  export let summary: any = null;
  export let invitation: any = null;
  export let loading = false;
  export let error = "";
  export let acting = false;
  export let actingAction: "accept" | "reject" | null = null;
  export let actionsDisabled = false;
  export let onClose: () => void = () => {};
  export let onAccept: (summary: any) => void = () => {};
  export let onReject: (summary: any) => void = () => {};

  const formatDate = (value?: string): string => {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  const resolveBadgeClass = (status?: string): string => {
    if (status === "ACCEPTED") return "bg-green-500/20 text-white border-green-400/50 shadow-[0_0_0_1px_rgba(74,222,128,0.1)]";
    if (status === "REJECTED") return "bg-red-500/20 text-white border-red-400/50 shadow-[0_0_0_1px_rgba(248,113,113,0.1)]";
    if (status === "EXPIRED") return "bg-slate-400/20 text-white border-slate-300/40 shadow-[0_0_0_1px_rgba(203,213,225,0.08)]";
    return "bg-amber-500/25 text-white border-amber-300/50 shadow-[0_0_0_1px_rgba(251,191,36,0.12)]";
  };

  let current: any = null;
  let canAct = false;

  $: current = invitation ?? summary;
  $: canAct = !!current?.id && current?.status === "PENDING";

  const handleAccept = () => {
    if (summary) onAccept(summary);
  };

  const handleReject = () => {
    if (summary) onReject(summary);
  };

  const handleClose = () => onClose();

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<div
  class="fixed inset-0 bg-black/50 z-[1000] flex justify-end animate-[fadeIn_0.2s]"
  on:click={handleClose}
  on:keydown={handleKeydown}
  role="button"
  tabindex="0"
>
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="w-[500px] max-w-[90vw] bg-[#24283a] shadow-[-4px_0_20px_rgba(0,0,0,0.5)] flex flex-col animate-[slideIn_0.3s]"
    on:click|stopPropagation
    role="dialog"
    aria-modal="true"
  >
    <div class="flex justify-between items-start p-6 border-b border-white/10 bg-[#1b1e2e] gap-4">
      <div class="flex-1 min-w-0">
        <p class="text-xs uppercase tracking-[0.2em] text-white/70 mb-2">ACE Invitation</p>
        <h3 class="text-[1.125rem] font-bold text-white leading-tight break-words">
          {current?.engagementTitle || "Engagement Invitation"}
        </h3>
        {#if current?.senderCompanyName}
          <p class="text-sm text-white/90 mt-2">From {current.senderCompanyName}</p>
        {/if}
      </div>
      <div class="flex items-center gap-3">
        <span
          class={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${resolveBadgeClass(
            current?.status
          )}`}
        >
          {current?.status || "UNKNOWN"}
        </span>
        <button
          class="bg-transparent border-none text-white/80 text-[1.5rem] cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded transition-all hover:bg-[#24283a]/10 hover:text-white"
          on:click={handleClose}
        >
          ✕
        </button>
      </div>
    </div>

    <div class="drawer-scroll flex-1 overflow-y-auto p-6 flex flex-col gap-4">
      {#if loading}
        <div class="bg-black/20 border border-white/10 rounded-xl p-6 text-white/90">
          Loading invitation details...
        </div>
      {:else if error}
        <div class="bg-red-500/10 border border-red-400/30 rounded-xl p-5 text-red-200">
          {error}
        </div>
      {/if}

      <div class="bg-black/20 border border-white/10 rounded-xl overflow-hidden">
        <div class="grid grid-cols-1 sm:grid-cols-2">
          <div class="px-5 py-4 border-b border-white/5">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Invitation ID</p>
            <p class="text-sm text-white break-all">{current?.id || "—"}</p>
          </div>
          <div class="px-5 py-4 border-b border-white/5 sm:border-l">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Engagement ID</p>
            <p class="text-sm text-white break-all">{current?.engagementId || "—"}</p>
          </div>
          <div class="px-5 py-4 border-b border-white/5">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Received</p>
            <p class="text-sm text-white">{formatDate(current?.invitationDate)}</p>
          </div>
          <div class="px-5 py-4 border-b border-white/5 sm:border-l">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Expires</p>
            <p class="text-sm text-white">{formatDate(current?.expirationDate)}</p>
          </div>
          <div class="px-5 py-4 border-b border-white/5">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Sender AWS Account</p>
            <p class="text-sm text-white break-all">{current?.senderAwsAccountId || "—"}</p>
          </div>
          <div class="px-5 py-4 border-b border-white/5 sm:border-l">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Receiver Alias</p>
            <p class="text-sm text-white">{current?.receiverAlias || "—"}</p>
          </div>
          <div class="px-5 py-4">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Customer</p>
            <p class="text-sm text-white">{invitation?.customerCompanyName || "—"}</p>
          </div>
          <div class="px-5 py-4 sm:border-l">
            <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-1">Project</p>
            <p class="text-sm text-white">{invitation?.projectTitle || "—"}</p>
          </div>
        </div>
      </div>

      {#if invitation?.invitationMessage}
        <section class="bg-black/20 border border-white/10 rounded-xl p-5">
          <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-2">Invitation Message</p>
          <p class="text-sm text-white/90 leading-7">{invitation.invitationMessage}</p>
        </section>
      {/if}

      {#if invitation?.engagementDescription}
        <section class="bg-black/20 border border-white/10 rounded-xl p-5">
          <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-2">Engagement Description</p>
          <p class="text-sm text-white/90 leading-7">{invitation.engagementDescription}</p>
        </section>
      {/if}

      {#if invitation?.projectBusinessProblem}
        <section class="bg-black/20 border border-white/10 rounded-xl p-5">
          <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-2">Customer Problem</p>
          <p class="text-sm text-white/90 leading-7">{invitation.projectBusinessProblem}</p>
        </section>
      {/if}

      {#if invitation?.receiverResponsibilities?.length}
        <section class="bg-black/20 border border-white/10 rounded-xl p-5">
          <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-3">Receiver Responsibilities</p>
          <div class="flex flex-wrap gap-2">
            {#each invitation.receiverResponsibilities as responsibility}
              <span class="px-3 py-1 rounded-full bg-[#24283a]/8 border border-white/10 text-sm text-white/90">
                {responsibility}
              </span>
            {/each}
          </div>
        </section>
      {/if}

      {#if invitation?.senderContacts?.length}
        <section class="bg-black/20 border border-white/10 rounded-xl p-5">
          <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-3">Sender Contacts</p>
          <div class="grid gap-3">
            {#each invitation.senderContacts as contact}
              <div class="rounded-lg border border-white/10 bg-[#24283a]/5 p-4">
                <p class="text-sm font-semibold text-white">
                  {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "Unknown Contact"}
                </p>
                {#if contact.businessTitle}
                  <p class="text-sm text-white/85 mt-1">{contact.businessTitle}</p>
                {/if}
                {#if contact.email}
                  <p class="text-sm text-white/90 mt-2 break-all">{contact.email}</p>
                {/if}
                {#if contact.phone}
                  <p class="text-sm text-white/85 mt-1">{contact.phone}</p>
                {/if}
              </div>
            {/each}
          </div>
        </section>
      {/if}

      {#if invitation?.expectedCustomerSpend?.length}
        <section class="bg-black/20 border border-white/10 rounded-xl p-5">
          <p class="text-xs uppercase tracking-[0.15em] text-white/70 mb-3">Expected Customer Spend</p>
          <div class="grid gap-3">
            {#each invitation.expectedCustomerSpend as spend}
              <div class="rounded-lg border border-white/10 bg-[#24283a]/5 p-4 text-sm text-white/90">
                <p>{[spend.amount, spend.currencyCode].filter(Boolean).join(" ") || "—"}</p>
                {#if spend.frequency}
                  <p class="text-white/85 mt-1">Frequency: {spend.frequency}</p>
                {/if}
                {#if spend.targetCompany}
                  <p class="text-white/85 mt-1">Target company: {spend.targetCompany}</p>
                {/if}
              </div>
            {/each}
          </div>
        </section>
      {/if}
    </div>

    {#if canAct}
      <div class="p-6 border-t border-white/10 bg-[#1b1e2e] flex flex-col-reverse sm:flex-row gap-3">
        <button
          class="px-4 py-3 rounded-lg border border-red-400/40 text-red-200 bg-red-500/10 hover:bg-red-500/15 transition-colors disabled:opacity-60"
          on:click={handleReject}
          disabled={acting || actionsDisabled}
        >
          {acting && actingAction === "reject" ? "Working..." : "Reject Invitation"}
        </button>
        <button
          class="px-4 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors disabled:opacity-60"
          on:click={handleAccept}
          disabled={acting || actionsDisabled}
        >
          {acting && actingAction === "accept" ? "Working..." : "Accept Invitation"}
        </button>
      </div>
    {/if}
  </div>
</div>

<style>
  .drawer-scroll {
    scrollbar-color: rgba(255, 255, 255, 0.28) #24283a;
  }

  .drawer-scroll::-webkit-scrollbar {
    width: 12px;
  }

  .drawer-scroll::-webkit-scrollbar-track {
    background: #24283a;
  }

  .drawer-scroll::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.22);
    border: 3px solid #24283a;
    border-radius: 9999px;
  }

  .drawer-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.32);
  }
</style>
